import * as React from 'react';

/** Where the user was when they left the overview for a detail page. */
interface RestorePoint {
  /** Which page of the table the row was on. */
  page: number;
  /** The row's id, as a string so any key type works. */
  rowId: string;
}

const storageKey = (pageKey: string) => `rowRestore_${pageKey}`;

const read = (pageKey: string): RestorePoint | null => {
  const raw = sessionStorage.getItem(storageKey(pageKey));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed?.rowId === 'string' && typeof parsed?.page === 'number' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Overview -> detail -> back, landing on the row the user left from.
 *
 * Anchored to the row's id rather than to a pixel offset, because the overviews
 * do not agree on what scrolls: Classes and Courses put the table in their own
 * scroll container, Partners and Facilities let the whole page scroll. Asking
 * the row to scroll itself into view works for both, and survives a change of
 * density or page size in between.
 *
 * The stored point is a one-shot handoff: written when the user clicks into a
 * detail page, consumed on the next mount of the overview. So arriving from the
 * nav menu restores nothing, which is what you want - it only fires on the way
 * back. If the row is not on screen (the list was refiltered, or the record was
 * deleted) the scroll is simply skipped.
 *
 * Call this above the page state, so `restoredPage` can seed it:
 *   const { restoredPage, rememberRow, rowProps } = useRowRestore('courses');
 *   const [page, setPage] = React.useState(restoredPage ?? 1);
 *
 * @param pageKey Unique, stable name for this overview.
 */
export const useRowRestore = (pageKey: string) => {
  // Read, do not consume: under StrictMode a lazy initialiser runs twice, and a
  // destructive read would throw the restore point away before it is used.
  const [restorePoint] = React.useState<RestorePoint | null>(() => read(pageKey));
  const hasScrolled = React.useRef(false);

  // Clearing on mount rather than after a successful scroll means a point that
  // never matches a row cannot linger and fire on some later, unrelated visit.
  React.useEffect(() => {
    sessionStorage.removeItem(storageKey(pageKey));
  }, [pageKey]);

  // Deliberately no dependency array. Rows arrive from the store a tick or two
  // after mount, so this retries after every render until the row is in the DOM,
  // then latches off. The work is one querySelector, and only while a restore
  // point is pending.
  React.useEffect(() => {
    if (!restorePoint || hasScrolled.current) return;

    const row = document.querySelector(`[data-restore-id="${CSS.escape(restorePoint.rowId)}"]`);
    if (!row) return;

    hasScrolled.current = true;
    // 'auto' rather than 'smooth': this is restoring a position, not a transition,
    // and an animation on arrival reads as the page jumping on its own.
    row.scrollIntoView({ block: 'center', behavior: 'auto' });
  });

  /** Call in the row's edit handler, immediately before navigating to the detail. */
  const rememberRow = React.useCallback((rowId: string | number | undefined, page: number) => {
    if (rowId === undefined) return;
    sessionStorage.setItem(storageKey(pageKey), JSON.stringify({ rowId: String(rowId), page }));
  }, [pageKey]);

  /** Spread onto the <Tr> so the row can be found again on the way back. */
  const rowProps = (rowId: string | number | undefined) => ({ 'data-restore-id': String(rowId) });

  return {
    /** The page the user was on, for overviews that reset pagination on mount. */
    restoredPage: restorePoint?.page ?? null,
    rememberRow,
    rowProps
  };
};
